import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import moment from "moment";
import graphqlToUserModel from "shared/util/graphqlToUserModel";
import noop from "shared/util/noop";

import { withRouter } from "react-router";
import withMessenger from "apps/messenger/containers/withMessenger";

import UserThumb from "shared/components/UserThumb";
import OnlineDot from "shared/components/OnlineDot";
import BoostBolt from "shared/components/BoostBolt";
import SanitizedText from "shared/components/SanitizedText";

import ReadStatus from "./ReadStatus";

import { getMessageOpener } from "../util/helpers";
import { OKCUPID_SYSTEM_USERID, ALICE_USERID } from "../util/constants";

import "./MessageRow.scss";

const ONE_MINUTE = 60000;
const timeSinceTimestamp = (timestamp) => moment(timestamp).fromNow();

const MessageRow = ({
	correspondent,
	threadid,
	isUnread,
	isInactive,
	sentTime,
	receivedTime,
	snippet,
	timestamp,
	inboxSize,
	picBorderWidth,
	openConversation,
	markConversationRead,
	onClick,
	history,
}) => {
	const { user } = correspondent;
	const isTwoWay = !!(sentTime && receivedTime);
	const showReplyIcon = isTwoWay && sentTime > receivedTime;
	const isFromAdmin = user.id === OKCUPID_SYSTEM_USERID || user.id === ALICE_USERID;

	const handleClick = () => {
		if (isUnread) {
			markConversationRead(user.id);
		}

		onClick();
	};

	// Time since the message was sent (e.g. "5 minutes ago").
	const [timeSinceMessage, setTimeSinceMessage] = useState(timeSinceTimestamp(timestamp));
	useEffect(() => {
		// Update the time since the message was sent.
		setTimeSinceMessage(timeSinceTimestamp(timestamp));

		// And again every minute.
		const interval = setInterval(() => {
			setTimeSinceMessage(timeSinceTimestamp(timestamp));
		}, ONE_MINUTE);

		// Clear the interval if the timestamp changes.
		return () => clearInterval(interval);
	}, [timestamp]);

	return (
		<button
			onClick={getMessageOpener({
				user: graphqlToUserModel(correspondent),
				onClick: handleClick,
				timestamp,
				inboxSize,
				isUnread,
				isTwoWay,
				threadid,
				openConversation,
				isInactive,
				history,
			})}
			className="messages-messagerow"
			aria-label={`Message from ${user.displayname}`}
		>
			<div className="messages-messagerow-status">
				<ReadStatus
					isUnread={isUnread}
					hasReplied={showReplyIcon}
				/>
			</div>

			<div className="messages-messagerow-photo">
				{user.username &&
					<UserThumb
						{...graphqlToUserModel(correspondent)}
						isAlice={user.userid === ALICE_USERID}
						username={user.displayname}
						indicatorWidth={picBorderWidth}
						shouldDisableAnimationMessages
						shouldDisableAnimationLikes
					/>
				}
			</div>

			<div className="messages-messagerow-main">
				<div className="messages-messagerow-main-top-line">
					<div className="messages-messagerow-main-top-line-aboutuser">
						<span className="messages-messagerow-main-top-line-aboutuser-username">
							{user.displayname}
							{user.age && user.id !== OKCUPID_SYSTEM_USERID ? `, ${user.age}` : ""}
						</span>

						{user.isOnline &&
							<OnlineDot className="messages-messagerow-main-top-line-aboutuser-onlinestatus-dot" />
						}
						{correspondent.targetLikeViaSpotlight &&
							<BoostBolt className="messages-messagerow-main-top-line-aboutuser-viaBoost-dot" />
						}
					</div>

					<div className="messages-messagerow-main-top-line-timestamp">
						{timeSinceMessage}
					</div>
				</div>

				<SanitizedText
					className={classnames("messages-messagerow-main-snippet", {
						"messages-messagerow-main-snippet--reply": showReplyIcon,
					})}
					text={snippet}
					shouldDangerouslySetInnerHTML={isFromAdmin}
				/>
			</div>

		</button>
	);
};

MessageRow.propTypes = {
	snippet: PropTypes.node,
	timestamp: PropTypes.number,

	threadid: PropTypes.string,
	isUnread: PropTypes.bool,
	isInactive: PropTypes.bool,
	sentTime: PropTypes.number,
	receivedTime: PropTypes.number,
	correspondent: PropTypes.object,
	inboxSize: PropTypes.number,

	picBorderWidth: PropTypes.number,
	onClick: PropTypes.func,

	markConversationRead: PropTypes.func,
	openConversation: PropTypes.func,
	history: PropTypes.object,
};

MessageRow.defaultProps = {
	snippet: null,
	timestamp: null,
	threadid: null,
	isUnread: null,
	isInactive: null,
	sentTime: 0,
	receivedTime: 0,
	correspondent: {},
	inboxSize: null,
	picBorderWidth: 3,
	onClick: noop,
	markConversationRead: noop,
	openConversation: noop,
	history: null,
};

export default withMessenger(MessageRow);

export const MobileMessageRow = withRouter(MessageRow);



// WEBPACK FOOTER //
// ./src/apps/messages/components/MessageRow.jsx